import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';

const WEEKDAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MONTH_LABELS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];
const MONTH_SHORT_LABELS = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez',
];

export interface CalendarDay {
  date: Date;
  day: number;
  currentMonth: boolean;
  today: boolean;
  selected: boolean;
  isStart: boolean;
  isEnd: boolean;
  inRange: boolean;
  disabled: boolean;
}

export type CalendarView = 'days' | 'months' | 'years';

@Component({
  selector: 'app-calendar-panel',
  templateUrl: './calendar-panel.component.html',
  styleUrls: ['./calendar-panel.component.scss'],
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarPanelComponent implements OnChanges {
  @Input() viewYear = new Date().getFullYear();
  @Input() viewMonth = new Date().getMonth();
  @Input() min: Date | null = null;
  @Input() max: Date | null = null;

  /** Single mode */
  @Input() selectedDate: Date | null = null;

  /** Range mode */
  @Input() rangeStart: Date | null = null;
  @Input() rangeEnd: Date | null = null;
  @Input() rangeHover: Date | null = null;
  @Input() isRangeMode = false;
  @Input() isSelectingEnd = false;

  @Output() dateSelect = new EventEmitter<Date>();
  @Output() dateHover = new EventEmitter<Date>();
  @Output() viewMonthChange = new EventEmitter<number>();
  @Output() viewYearChange = new EventEmitter<number>();

  readonly weekdays = WEEKDAY_LABELS;
  readonly monthLabels = MONTH_LABELS;
  readonly monthShortLabels = MONTH_SHORT_LABELS;

  calendarDays: CalendarDay[] = [];
  currentView: CalendarView = 'days';
  yearPageStart = 0;
  years: number[] = [];

  get headerLabel(): string {
    if (this.currentView === 'years') {
      return `${this.yearPageStart} – ${this.yearPageStart + 11}`;
    }
    if (this.currentView === 'months') {
      return String(this.viewYear);
    }
    return `${MONTH_LABELS[this.viewMonth]} ${this.viewYear}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['viewYear'] || changes['viewMonth'] ||
      changes['selectedDate'] || changes['rangeStart'] ||
      changes['rangeEnd'] || changes['rangeHover'] ||
      changes['isSelectingEnd'] || changes['min'] || changes['max']
    ) {
      this.buildCalendarDays();
    }
  }

  trackByDay(_index: number, cell: CalendarDay): number {
    return cell.date.getTime();
  }

  trackByIndex(index: number): number {
    return index;
  }

  // --- Header navigation ---

  toggleView(): void {
    if (this.currentView === 'days') {
      this.currentView = 'months';
    } else if (this.currentView === 'months') {
      this.yearPageStart = this.viewYear - (this.viewYear % 12);
      this.buildYears();
      this.currentView = 'years';
    } else {
      this.currentView = 'days';
    }
  }

  prev(): void {
    if (this.currentView === 'days') {
      this.prevMonth();
    } else if (this.currentView === 'months') {
      this.viewYear--;
      this.viewYearChange.emit(this.viewYear);
    } else {
      this.yearPageStart -= 12;
      this.buildYears();
    }
  }

  next(): void {
    if (this.currentView === 'days') {
      this.nextMonth();
    } else if (this.currentView === 'months') {
      this.viewYear++;
      this.viewYearChange.emit(this.viewYear);
    } else {
      this.yearPageStart += 12;
      this.buildYears();
    }
  }

  // --- Month view ---

  selectMonth(month: number): void {
    this.viewMonth = month;
    this.viewMonthChange.emit(this.viewMonth);
    this.currentView = 'days';
    this.buildCalendarDays();
  }

  isCurrentMonth(month: number): boolean {
    const now = new Date();
    return this.viewYear === now.getFullYear() && month === now.getMonth();
  }

  isSelectedMonth(month: number): boolean {
    const ref = this.isRangeMode ? this.rangeStart : this.selectedDate;
    if (!ref) return false;
    return this.viewYear === ref.getFullYear() && month === ref.getMonth();
  }

  // --- Year view ---

  selectYear(year: number): void {
    this.viewYear = year;
    this.viewYearChange.emit(this.viewYear);
    this.currentView = 'months';
  }

  isCurrentYear(year: number): boolean {
    return year === new Date().getFullYear();
  }

  isSelectedYear(year: number): boolean {
    const ref = this.isRangeMode ? this.rangeStart : this.selectedDate;
    if (!ref) return false;
    return year === ref.getFullYear();
  }

  // --- Day view ---

  onDayClick(cell: CalendarDay): void {
    if (cell.disabled) return;
    this.dateSelect.emit(cell.date);
  }

  onDayHover(cell: CalendarDay): void {
    if (!cell.disabled) {
      this.dateHover.emit(cell.date);
    }
  }

  // --- Private helpers ---

  private prevMonth(): void {
    if (this.viewMonth === 0) {
      this.viewMonth = 11;
      this.viewYear--;
      this.viewYearChange.emit(this.viewYear);
    } else {
      this.viewMonth--;
    }
    this.viewMonthChange.emit(this.viewMonth);
    this.buildCalendarDays();
  }

  private nextMonth(): void {
    if (this.viewMonth === 11) {
      this.viewMonth = 0;
      this.viewYear++;
      this.viewYearChange.emit(this.viewYear);
    } else {
      this.viewMonth++;
    }
    this.viewMonthChange.emit(this.viewMonth);
    this.buildCalendarDays();
  }

  private buildYears(): void {
    this.years = [];
    for (let i = 0; i < 12; i++) {
      this.years.push(this.yearPageStart + i);
    }
  }

  private buildCalendarDays(): void {
    const today = new Date();
    const firstOfMonth = new Date(this.viewYear, this.viewMonth, 1);
    const startDay = firstOfMonth.getDay();
    const days: CalendarDay[] = [];

    for (let i = 0; i < 42; i++) {
      const date = new Date(this.viewYear, this.viewMonth, 1 - startDay + i);
      const time = date.getTime();

      let selected = false;
      let isStart = false;
      let isEnd = false;
      let inRange = false;

      if (this.isRangeMode) {
        isStart = this.rangeStart ? this.isSameDay(date, this.rangeStart) : false;
        isEnd = this.rangeEnd ? this.isSameDay(date, this.rangeEnd) : false;

        if (this.rangeStart) {
          const startTime = this.stripTime(this.rangeStart).getTime();
          if (this.rangeEnd) {
            const endTime = this.stripTime(this.rangeEnd).getTime();
            inRange = time > startTime && time < endTime;
          } else if (this.isSelectingEnd && this.rangeHover) {
            const hoverTime = this.stripTime(this.rangeHover).getTime();
            if (hoverTime >= startTime) {
              inRange = time > startTime && time < hoverTime;
            }
          }
        }
      } else {
        selected = this.selectedDate ? this.isSameDay(date, this.selectedDate) : false;
      }

      days.push({
        date,
        day: date.getDate(),
        currentMonth: date.getMonth() === this.viewMonth,
        today: this.isSameDay(date, today),
        selected,
        isStart,
        isEnd,
        inRange,
        disabled: this.isDateDisabled(date),
      });
    }

    this.calendarDays = days;
  }

  private isSameDay(a: Date, b: Date): boolean {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  private isDateDisabled(date: Date): boolean {
    if (this.min && date < this.stripTime(this.min)) return true;
    if (this.max && date > this.stripTime(this.max)) return true;
    return false;
  }

  private stripTime(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }
}
